'use client';

import { useQuery, useQueryClient } from '@zg-rentals/react-query5';
import { useLocale } from 'next-intl';
import type { SupportedLocale } from '@/src/i18n/routing';
import { type ExtendedListing, listing } from '@/src/lib/api/endpoints/listing';
import type { DeepPartial } from '@/src/lib/api/wire';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { listingDetailKeys, normalizeListingDetailUnit } from '@/src/lib/hooks/query/queryKeys';
import { adaptV3SimilarListings, getEmbeddedV3SimilarListings } from '@/src/lib/utils/v3ListingDetail';

// The v3 detail response embeds similar listings as search cards; adaptV3SimilarListings maps each one with the
// SRP's toCardListing and adds the price summary fields the comparison table and post-contact cards read.
export type SimilarListing = DeepPartial<ExtendedListing> & {
  name?: string;
  title?: string;
  uriBuilding?: string;
  pricing?: { summary?: { priceLow?: number; priceHigh?: number } };
  isAllowedToRetain?: boolean;
};

export const similarListingsKeys = {
  all: ['halo', 'similarListings'] as const,
  byMaloneLotId: (maloneLotIdEncoded: string | null, unit: string | null = null) =>
    ['halo', 'similarListings', 'byMaloneLotId', maloneLotIdEncoded ?? '', unit ?? ''] as const,
};

export function useSimilarListings(params: { maloneLotIdEncoded: string | null | undefined; unit?: string | null }) {
  const { maloneLotIdEncoded, unit = null } = params;
  const normalizedUnit = normalizeListingDetailUnit(unit);
  const queryClient = useQueryClient();
  const locale = useLocale() as SupportedLocale;
  const cachedListing = maloneLotIdEncoded
    ? (queryClient.getQueryData<PropertyDetailListing>(
        listingDetailKeys.byMaloneLotId(maloneLotIdEncoded, 'unit', normalizedUnit, locale),
      ) ??
      queryClient.getQueryData<PropertyDetailListing>(
        listingDetailKeys.byMaloneLotId(maloneLotIdEncoded, 'building', normalizedUnit, locale),
      ) ??
      queryClient.getQueryData<PropertyDetailListing>(
        listingDetailKeys.byMaloneLotId(maloneLotIdEncoded, 'unit', null, locale),
      ) ??
      queryClient.getQueryData<PropertyDetailListing>(
        listingDetailKeys.byMaloneLotId(maloneLotIdEncoded, 'building', null, locale),
      ))
    : undefined;
  const embeddedSimilarListings = getEmbeddedV3SimilarListings(cachedListing);

  return useQuery<Array<SimilarListing>>({
    queryKey: similarListingsKeys.byMaloneLotId(maloneLotIdEncoded ?? null, normalizedUnit),
    initialData: embeddedSimilarListings,
    queryFn: async () => {
      if (!maloneLotIdEncoded) return [];
      return adaptV3SimilarListings(await listing.fetchListingDetailV3(maloneLotIdEncoded, { unit: normalizedUnit }));
    },
    enabled: Boolean(maloneLotIdEncoded) && !embeddedSimilarListings,
    staleTime: 5 * 60 * 1000,
    meta: {
      tags: ['similarListings'],
      context: {
        api: 'hotpads-api',
        details: 'fetch similar listings',
      },
    },
  });
}
